/**
 * DashboardSignups — /website-builder/signups
 *
 * Real subscriber list backed by /api/builder/signups (newsletter signups
 * captured via public SignupBlock forms on the rendered builder site).
 *   GET    /api/builder/signups[?search=&source=]
 *   POST   /api/builder/signups           {email, name?, tags?, source?}
 *   DELETE /api/builder/signups/{id}
 *   GET    /api/builder/signups/export.csv
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Mail, Users, Download, Search, Plus, Trash2, Loader2, Tag, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function StatCard({ label, value, icon: Icon, ramp = 'violet' }) {
  const RAMPS = {
    violet:  'from-violet-500/20 to-violet-700/0  text-violet-300',
    cyan:    'from-cyan-500/20    to-cyan-700/0    text-cyan-300',
    emerald: 'from-emerald-500/20 to-emerald-700/0 text-emerald-300',
  };
  const cls = RAMPS[ramp] || RAMPS.violet;
  return (
    <div className="bg-[#15152E] border border-white/[0.06] rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10.5px] uppercase tracking-[0.18em] text-white/55 font-bold">{label}</p>
        <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${cls} flex items-center justify-center`}>
          <Icon size={15} />
        </div>
      </div>
      <p className="text-3xl font-bold text-white font-syne tracking-tight">{value}</p>
    </div>
  );
}

function AddSubModal({ open, onClose, onAdded }) {
  const [email, setEmail] = useState('');
  const [name, setName]   = useState('');
  const [tags, setTags]   = useState('');
  const [busy, setBusy]   = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!email.includes('@')) { toast.error('Valid email required'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/builder/signups`, {
        email, name, tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      }, { headers: headers() });
      onAdded(r.data);
      toast.success('Subscriber added');
      setEmail(''); setName(''); setTags('');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[#15152E] border border-white/10 rounded-2xl p-6 w-full max-w-md" data-testid="signup-add-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#6D28D9] flex items-center justify-center"><Mail size={16} className="text-white" /></div>
            <h3 className="text-white font-bold font-syne text-lg">Add subscriber</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md text-white/55 hover:text-white hover:bg-white/5" data-testid="signup-add-close"><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-[#A5A8C9] text-[11px]">Email *</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="visitor@example.com" className="bg-white/5 border-white/10 text-white text-sm mt-1.5 rounded-xl" data-testid="signup-add-email" />
          </div>
          <div>
            <Label className="text-[#A5A8C9] text-[11px]">Name (optional)</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jordan" className="bg-white/5 border-white/10 text-white text-sm mt-1.5 rounded-xl" data-testid="signup-add-name" />
          </div>
          <div>
            <Label className="text-[#A5A8C9] text-[11px]">Tags (comma-separated)</Label>
            <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="vip, beta-tester" className="bg-white/5 border-white/10 text-white text-sm mt-1.5 rounded-xl" data-testid="signup-add-tags" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="px-4 py-2 text-[11px] uppercase tracking-wider font-bold text-white/65 hover:text-white">Cancel</button>
          <button type="submit" disabled={busy} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-[11px] font-bold uppercase tracking-wider disabled:opacity-50 flex items-center gap-2" data-testid="signup-add-submit">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add
          </button>
        </div>
      </form>
    </div>
  );
}

export default function DashboardSignups() {
  const [subs, setSubs]       = useState([]);
  const [sources, setSources] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [filter, setFilter]   = useState('');
  const [openAdd, setOpenAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/builder/signups`, {
        headers: headers(),
        params: { search, source: filter },
      });
      setSubs(r.data?.items || []);
      setSources(r.data?.sources || {});
    } catch { toast.error('Failed to load subscribers'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [search, filter]);

  const remove = async (sub) => {
    if (!(await themedConfirm(`Remove ${sub.email} from your list?`))) return;
    try {
      await axios.delete(`${API}/builder/signups/${sub.id}`, { headers: headers() });
      setSubs((arr) => arr.filter((x) => x.id !== sub.id));
      toast.success('Subscriber removed');
    } catch (e) { toast.error(e.response?.data?.detail || 'Remove failed'); }
  };

  const exportCsv = () => {
    const url = `${API}/builder/signups/export.csv`;
    fetch(url, { headers: headers() })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `subscribers_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        toast.success('CSV downloaded');
      })
      .catch(() => toast.error('Export failed'));
  };

  const total = useMemo(() => Object.values(sources).reduce((s, n) => s + n, 0), [sources]);
  const last30 = useMemo(() => {
    const cut = new Date(Date.now() - 30 * 86400e3).toISOString();
    return subs.filter((s) => (s.created_at || '') > cut).length;
  }, [subs]);

  return (
    <div className="p-10 max-w-5xl" data-testid="signups-page">
      <div className="flex items-start justify-between gap-4 mb-7 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#A78BFA] font-bold mb-2">Marketing</p>
          <h1 className="text-3xl font-bold text-white font-syne tracking-tight">Subscribers</h1>
          <p className="text-[#A5A8C9] text-sm mt-1.5">Everyone who's ever opted in via a signup form on your site.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} className="px-4 py-2 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] text-white text-[11px] font-bold uppercase tracking-wider flex items-center gap-2" data-testid="signups-export">
            <Download size={12} /> Export CSV
          </button>
          <button onClick={() => setOpenAdd(true)} className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-[11px] font-bold uppercase tracking-wider flex items-center gap-2 hover:shadow-[0_12px_30px_rgba(139,92,246,0.45)]" data-testid="signups-add">
            <Plus size={12} /> Add subscriber
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <StatCard label="Total subscribers" value={total}  icon={Users} ramp="violet" />
        <StatCard label="Last 30 days"      value={last30} icon={Mail}  ramp="cyan"   />
        <StatCard label="Sources"           value={Object.keys(sources).length} icon={Tag} ramp="emerald" />
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by email or name…" className="bg-white/5 border-white/10 text-white text-sm pl-9 rounded-xl" data-testid="signups-search" />
        </div>
        <button onClick={() => setFilter('')} className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all ${filter === '' ? 'bg-violet-500/15 text-violet-200 border border-violet-400/30' : 'bg-white/[0.04] text-white/65 border border-white/8 hover:bg-white/[0.08]'}`} data-testid="signups-filter-all">All ({total})</button>
        {Object.entries(sources).map(([src, count]) => (
          <button key={src} onClick={() => setFilter(src)} className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all ${filter === src ? 'bg-violet-500/15 text-violet-200 border border-violet-400/30' : 'bg-white/[0.04] text-white/65 border border-white/8 hover:bg-white/[0.08]'}`} data-testid={`signups-filter-${src}`}>
            {src} ({count})
          </button>
        ))}
      </div>

      <div className="bg-[#15152E] border border-white/[0.06] rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center gap-2 text-white/55"><Loader2 size={14} className="animate-spin" /> Loading subscribers…</div>
        ) : subs.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-14 h-14 rounded-full bg-[#A78BFA]/10 flex items-center justify-center mx-auto mb-4"><Mail size={22} className="text-[#A78BFA]" /></div>
            <h3 className="text-[16px] font-bold text-white font-syne">No subscribers yet</h3>
            <p className="text-[#9CA3AF] text-[12.5px] mt-1 max-w-md mx-auto">Drop a Signup block on your site (Pages → Edit → Add Section → Signup) and visitors will appear here automatically.</p>
          </div>
        ) : (
          <table className="w-full" data-testid="signups-table">
            <thead className="bg-white/[0.02] border-b border-white/[0.06]">
              <tr>
                <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider font-bold text-white/55">Email</th>
                <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider font-bold text-white/55">Name</th>
                <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider font-bold text-white/55">Source</th>
                <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider font-bold text-white/55">Tags</th>
                <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider font-bold text-white/55">Joined</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => (
                <tr key={s.id} className="border-b border-white/[0.04] hover:bg-white/[0.02]" data-testid={`signup-row-${s.id}`}>
                  <td className="px-5 py-3 text-[13px] text-white">{s.email}</td>
                  <td className="px-5 py-3 text-[12.5px] text-white/70">{s.name || '—'}</td>
                  <td className="px-5 py-3 text-[11px] uppercase tracking-wider text-violet-300 font-bold">{s.source || '—'}</td>
                  <td className="px-5 py-3 text-[11px] text-white/65">
                    {(s.tags || []).map((t) => <span key={t} className="inline-block mr-1 mb-1 px-1.5 py-0.5 rounded bg-white/8 text-white/70 text-[10px]">{t}</span>)}
                    {(!s.tags || s.tags.length === 0) && <span className="text-white/30">—</span>}
                  </td>
                  <td className="px-5 py-3 text-[11.5px] text-white/55">{s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => remove(s)} className="p-1.5 rounded-md text-white/55 hover:text-red-400 hover:bg-red-500/10 transition-colors" data-testid={`signup-delete-${s.id}`}><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AddSubModal open={openAdd} onClose={() => setOpenAdd(false)} onAdded={(row) => { setSubs((arr) => [row, ...arr.filter((x) => x.id !== row.id)]); load(); }} />
    </div>
  );
}
